export const bigQueryProjectSelectOptions = (projects) => projects.map((resource) => ({value: resource.projectId, label: `[${resource.projectId}] ${resource.displayName}`}));
export const bigQueryDatasetSelectOptions = (datasets) => datasets.map((resource) => ({value: `${resource.projectId}:${resource.datasetId}`, label: `[${resource.projectId}.${resource.datasetId}] ${resource.displayName} (${resource.location})`}));
export const analyticsPropertySelectOptions = (properties) => properties.map((property) => ({value: property.propertyId, label: `[${property.propertyId}] ${property.displayName} — ${property.accountDisplayName} (${property.timeZone}, ${property.currencyCode})`}));
export const bigQueryDatasetKey = (resource) => `${resource.projectId}:${resource.datasetId}`;
export const withDefaultBigQueryPropertyTimeZones = (currentTimeZones, selectedDatasetKeys, defaultTimeZone = 'Europe/Prague') => selectedDatasetKeys.reduce((timeZones, datasetKey) => {
    if (Object.prototype.hasOwnProperty.call(timeZones, datasetKey)) return timeZones;
    return {...timeZones, [datasetKey]: defaultTimeZone};
}, currentTimeZones);
export const hydrateBigQueryPropertyTimeZones = (storedResources) => Object.fromEntries(storedResources
    .filter((resource) => resource.parentDisplayName && resource.providerId)
    .map((resource) => [`${resource.parentDisplayName}:${resource.providerId}`, resource.propertyTimeZone ?? null]));
export const hydrateBigQueryDraft = (storedResources, preservedDraft = null) => preservedDraft ?? {
    selectedProjects: [...new Set(storedResources.map((resource) => resource.parentDisplayName).filter(Boolean))],
    selectedDatasets: storedResources.filter((resource) => resource.parentDisplayName && resource.providerId).map((resource) => `${resource.parentDisplayName}:${resource.providerId}`),
    propertyTimeZones: hydrateBigQueryPropertyTimeZones(storedResources),
};
export const reconcileBigQueryDraftWithDiscovery = (draft, projects, datasets) => {
    const availableProjectIds = new Set(projects.map((project) => project.projectId));
    const availableDatasetKeys = new Set(datasets.map(bigQueryDatasetKey));
    return {
        selectedProjects: draft.selectedProjects.filter((projectId) => availableProjectIds.has(projectId)),
        selectedDatasets: draft.selectedDatasets.filter((datasetKey) => availableDatasetKeys.has(datasetKey)),
        propertyTimeZones: Object.fromEntries(Object.entries(draft.propertyTimeZones).filter(([datasetKey]) => availableDatasetKeys.has(datasetKey))),
    };
};
export const reloadIntegrationDetailWithDraftCleanup = async (reloadIntegrationDetail, clearDraft) => {
    try {
        return await reloadIntegrationDetail();
    } catch (caught) {
        clearDraft();
        throw caught;
    }
};
export const bigQueryRecipeInputFromProviderResource = (resource) => ({
    billingProjectId: resource.parentDisplayName,
    dataProjectId: resource.parentDisplayName,
    datasetId: resource.providerId,
    location: resource.timeZone,
    maximumBytesBilled: resource.maximumBytesBilled,
    dailyBytesLimit: resource.dailyBytesLimit ?? null,
    monthlyBytesLimit: resource.monthlyBytesLimit ?? null,
    propertyTimeZone: resource.propertyTimeZone ?? null,
});
export const pendingBigQueryRecipeInputs = (storedResources, pendingStatus) => storedResources
    .filter((resource) => resource.bigQueryRecipeStatus === pendingStatus)
    .map(bigQueryRecipeInputFromProviderResource);
export const revalidatePendingBigQueryRecipes = async (api, requestContext, storedResources, pendingStatus, reloadIntegrationDetail) => {
    const inputs = pendingBigQueryRecipeInputs(storedResources, pendingStatus);
    if (inputs.length === 0) return [];
    const results = [];
    let requestError = null;
    try {
        for (const input of inputs) {
            results.push(await api.validateAndSelectGoogleBigQueryGA4ExportRecipe({
                ...requestContext,
                googleBigQueryRecipeInput: input,
            }));
        }
    } catch (caught) {
        requestError = caught;
    }
    try {
        await reloadIntegrationDetail();
    } catch (reloadError) {
        if (requestError === null) throw reloadError;
    }
    if (requestError !== null) throw requestError;
    return results;
};
export const summarizeBigQueryRecipeValidationResults = (results, pendingStatus, readyStatus) => {
    const pendingCount = results.filter((result) => result?.status === pendingStatus).length;
    const readyEstimates = results
        .filter((result) => result?.status === readyStatus && result.estimatedBytesProcessed != null)
        .map((result) => Number(result.estimatedBytesProcessed))
        .filter(Number.isFinite);
    return {
        pendingCount,
        readyCount: results.filter((result) => result?.status === readyStatus).length,
        readyEstimatedBytes: readyEstimates.length > 0 ? readyEstimates.reduce((sum, estimate) => sum + estimate, 0) : null,
    };
};
export const bigQueryRecipeSaveOutcome = ({pendingCount, readyCount}) => {
    if (pendingCount > 0 && readyCount > 0) return 'mixed';
    if (pendingCount > 0) return 'pending';
    if (readyCount > 0) return 'ready';
    return 'saved';
};
export const bigQueryMixedSelectionTranslationValues = (summary) => ({
    readyCount: summary.readyCount,
    pendingCount: summary.pendingCount,
    estimatedBytesProcessed: summary.readyEstimatedBytes ?? '—',
});
export const selectedBigQueryRecipeInputs = (datasets, selectedKeys, maximumBytesBilled, dailyBytesLimit = null, monthlyBytesLimit = null, propertyTimeZones = {}) => datasets
    .filter((resource) => selectedKeys.includes(bigQueryDatasetKey(resource)))
    .map((dataset) => {
        const propertyTimeZone = propertyTimeZones[bigQueryDatasetKey(dataset)];
        return {
            billingProjectId: dataset.projectId,
            dataProjectId: dataset.projectId,
            datasetId: dataset.datasetId,
            location: dataset.location,
            maximumBytesBilled,
            dailyBytesLimit,
            monthlyBytesLimit,
            propertyTimeZone: typeof propertyTimeZone === 'string' && propertyTimeZone.trim() !== '' ? propertyTimeZone : null,
        };
    });
export const formatMegabytesInput = (value) => {
    const digits = String(value ?? '').replace(/\s/g, '').replace(/\D/g, '');
    return digits.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
};
export const megabytesToBytes = (megabytes) => Math.round(Number(String(megabytes).replace(/\s/g, '')) * 1000000);
export const bytesToMegabytes = (bytes, fallback) => formatMegabytesInput(bytes == null ? fallback : Math.round(Number(bytes) / 1000000));
export const formatProcessedBytes = (bytes = 0, locale) => {
    const processedBytes = Number(bytes);
    if (!Number.isFinite(processedBytes) || processedBytes <= 0) return '0 MB';

    const format = (value, maximumFractionDigits) => value.toLocaleString(locale, {maximumFractionDigits});
    if (processedBytes < 1000) return `${format(processedBytes, 0)} B`;
    if (processedBytes < 1000000) return `${format(processedBytes / 1000, 2)} kB`;
    if (processedBytes < 1000000000) return `${format(processedBytes / 1000000, 1)} MB`;
    return `${format(processedBytes / 1000000000, 2)} GB`;
};
export const loadGoogleIntegrationUsage = (api, scope) => api.getGoogleDataIntegrationUsage(scope);
export const hydrateBigQuerySelection = (currentProjects, currentDatasets, storedResources) => {
    const storedDatasets = storedResources.filter((resource) => resource.parentDisplayName && resource.providerId).map((resource) => ({
        projectId: resource.parentDisplayName,
        datasetId: resource.providerId,
        displayName: resource.displayName,
        location: resource.timeZone,
        resourceId: resource.resourceId ?? null,
        bigQueryRecipeStatus: resource.bigQueryRecipeStatus ?? null,
    }));
    const projects = [...new Map([...currentProjects, ...storedDatasets.map((resource) => ({projectId: resource.projectId, displayName: resource.projectId}))].map((resource) => [resource.projectId, resource])).values()];
    const datasets = [...new Map([...currentDatasets, ...storedDatasets].map((resource) => [`${resource.projectId}:${resource.datasetId}`, resource])).values()];
    return {projects, datasets, selectedProjects: [...new Set(storedDatasets.map((resource) => resource.projectId))], selectedDatasets: storedDatasets.map((resource) => `${resource.projectId}:${resource.datasetId}`)};
};
